import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { getPublicQuoteStrings, type PublicQuoteLocale } from '@/lib/publicQuoteLocale';

interface RespondDialogProps {
  open: 'accepted' | 'declined' | null;
  onOpenChange: (open: 'accepted' | 'declined' | null) => void;
  signerName: string;
  onSignerNameChange: (name: string) => void;
  message: string;
  onMessageChange: (msg: string) => void;
  responding: boolean;
  onRespond: () => void;
  locale: PublicQuoteLocale;
}

export default function RespondDialog({
  open,
  onOpenChange,
  signerName,
  onSignerNameChange,
  message,
  onMessageChange,
  responding,
  onRespond,
  locale,
}: RespondDialogProps) {
  const t = getPublicQuoteStrings(locale).respondDialog;

  return (
    <Dialog open={!!open} onOpenChange={() => onOpenChange(null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{open === 'accepted' ? t.acceptTitle : t.declineTitle}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground mb-4">
          {open === 'accepted' ? t.acceptIntro : t.declineIntro}
        </p>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="signerName">{t.nameLabel}</Label>
            <Input
              id="signerName"
              value={signerName}
              onChange={(e) => onSignerNameChange(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="message">
              {open === 'accepted' ? t.remarkOptional : t.declineReasonOptional}
            </Label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => onMessageChange(e.target.value)}
              rows={3}
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(null)}>
            {t.cancel}
          </Button>
          <Button
            variant={open === 'accepted' ? 'default' : 'destructive'}
            onClick={onRespond}
            disabled={responding}
          >
            {responding ? t.busy : open === 'accepted' ? t.accept : t.decline}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
